import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowUpRight, Camera, Cloud, Cursor, Eraser, Scribble, TextT, Trash, X,
} from '@phosphor-icons/react';
import type { Foto, Marca, MarcaData } from '@/api/laminas';
import { parsearMarcasFoto } from '@/api/laminas';
import { CapaMarcas } from './CapaMarcas';
import { BarraVertical } from './BarraVertical';
import type { BotonBarra } from './BarraVertical';
import {
  DE_TEXTO, DE_TRAZO, GROSORES, PALETA_COLORES,
  adelgazarTrazo, featureDeArrastre, featureDeTexto, featureDeTrazo, reestilar, trasladar,
} from './marcado';
import type { Herramienta } from './marcado';

// F-10 de ARQ-007: ANOTAR LA FOTO DE OBRA.
//
// Pedido de Daniel (20-oct-2026): «lo que hace falta es la función de
// anotaciones sobre las fotos que tomo en el momento». Reemplaza al visor de
// solo lectura que vivía en `Chinchetas.tsx`, que abría la foto y no dejaba
// dibujar encima.
//
// LAS TRES DECISIONES QUE TOMÓ DANIEL (21-oct-2026), y qué significan acá:
//
// 1. VECTORIAL, la foto original nunca se toca. Procore ofrece «Sobrescribir» /
//    «Guardar como foto nueva» y las dos queman los píxeles; acá el dibujo se
//    guarda como JSON en `LamFotos.MarcasJson` y la imagen que subió el celular
//    queda intacta. Un círculo mal puesto se corrige sin volver a la obra.
// 2. SIN cámara dentro de la app: se sigue usando el selector de iOS y el
//    editor aparece cuando la foto ya está clavada. Por eso este componente no
//    sabe nada de capturar, solo de dibujar sobre algo que ya existe.
// 3. LA BARRA VA VERTICAL A LA DERECHA, y de acá pasa al plano. Vive en
//    `BarraVertical.tsx` justamente para eso: «una barra, dos lugares».
//
// NO TRAE UN SEGUNDO PINTOR. El dibujo se genera con el MISMO `marcado.ts` del
// plano y se pinta con el MISMO `CapaMarcas`. Eso es lo que hace que F-10 sea
// un archivo y no un editor nuevo: la geometría de la nube de revisión, el
// adelgazado del trazo libre, el traslado y el re-estilado ya estaban escritos
// y ya estaban probados. Lo propio de la foto es solo su espacio de coordenadas.
//
// LO QUE A PROPÓSITO NO TIENE:
//   · MEDIR (distancia y área). Una foto de obra no está calibrada ni es
//     ortogonal: un «3,40 m» leído sobre una perspectiva es un dato falso con
//     cara de dato bueno. Medir se queda en el plano, donde hay escala.
//   · ZOOM Y PAN. La foto entra completa y se dibuja encima. El día que haga
//     falta afinar un detalle se copia el gesto del visor, que ya lo resolvió;
//     adelantarlo serían dos manejos de puntero que mantener.
//   · COPIAR sin PEGAR. Procore muestra «Copiar» y «Duplicar»; acá quedó solo
//     Duplicar, que es lo mismo en un paso. Un «Copiar» sin lugar donde pegar
//     es un botón que no hace nada visible.

/** Las herramientas del editor. `borrar` no dibuja, así que no está en `marcado.ts`. */
type HerramientaFoto = Herramienta | 'borrar';

const BOTONES: BotonBarra<HerramientaFoto>[] = [
  { clave: 'mover', titulo: 'Seleccionar y mover', icono: <Cursor size={18} weight="bold" /> },
  { clave: 'libre', titulo: 'Trazo libre', icono: <Scribble size={18} weight="bold" /> },
  { clave: 'nube', titulo: 'Nube de revisión', icono: <Cloud size={18} weight="bold" /> },
  { clave: 'flecha', titulo: 'Flecha', icono: <ArrowUpRight size={18} weight="bold" /> },
  // Con recuadro y no texto pelado: sobre una foto de obra el texto suelto se
  // pierde contra el fondo, y el papel blanco del recuadro lo despega.
  { clave: 'textoCaja', titulo: 'Texto', icono: <TextT size={18} weight="bold" /> },
  { clave: 'borrar', titulo: 'Borrar un dibujo', icono: <Eraser size={18} weight="bold" /> },
];

/** Las que se dibujan arrastrando: bajar, mover, soltar. */
const DE_ARRASTRE_FOTO: HerramientaFoto[] = ['nube', 'flecha'];

/** Píxeles de la IMAGEN por debajo de los cuales un arrastre fue un toque. */
const MINIMO_PX = 6;

type Punto = [number, number];

/** Una marca suelta con la forma que `CapaMarcas` espera de una fila de la base. */
const comoFila = (d: MarcaData, id: number): Marca => ({
  id, hojaId: 0, data: JSON.stringify(d), privada: false, fechaActualizacion: '',
});

export function EditorFoto({ foto, onCerrar, onBorrar, onGuardar, guardando }: {
  foto: Foto;
  onCerrar: () => void;
  onBorrar?: (id: number) => void;
  /** Guarda el dibujo COMPLETO. Ver el PUT: no hay delta por marca. */
  onGuardar?: (idFoto: number, marcas: MarcaData[]) => void;
  guardando?: boolean;
}) {
  const [marcas, setMarcas] = useState<MarcaData[]>(() => parsearMarcasFoto(foto));
  const [herramienta, setHerramienta] = useState<HerramientaFoto>('libre');
  const [color, setColor] = useState(PALETA_COLORES[0]);
  const [grosor, setGrosor] = useState(GROSORES[1].valor);
  const [estilos, setEstilos] = useState(false);
  const [sel, setSel] = useState<number | null>(null);
  const [sucio, setSucio] = useState(false);
  /** El tamaño real de la imagen. Null hasta que carga: sin esto no hay espacio. */
  const [nat, setNat] = useState<{ w: number; h: number } | null>(null);
  /** Dónde quedó pintada la imagen dentro del marco, para calzarle el SVG encima. */
  const [caja2, setCaja2] = useState<{ x: number; y: number; w: number } | null>(null);

  const img = useRef<HTMLImageElement>(null);
  const caja = useRef<HTMLDivElement>(null);
  // El gesto vivo va en un ref y no en estado, igual que en el visor del plano:
  // un `setState` por `pointermove` re-renderiza el árbol entero a 60 Hz.
  const vivo = useRef<{ pts: Punto[] } | { mover: number; ox: number; oy: number } | null>(null);
  // Estas dos SÍ son estado, porque son lo que se ve mientras dura el gesto.
  const [previa, setPrevia] = useState<MarcaData | null>(null);
  const [corrida, setCorrida] = useState<{ id: number; dx: number; dy: number } | null>(null);

  /**
   * Mide dónde quedó pintada la imagen.
   *
   * El `<img>` se deja medir solo (`max-h-full max-w-full`, sin `inset-0`) a
   * propósito: así su caja ES la foto pintada y no el marco con franjas negras.
   * Con `absolute inset-0 object-contain` el `getBoundingClientRect()` devuelve
   * el marco entero, y el dibujo habría quedado corrido y escalado mal en toda
   * foto cuya proporción no fuera la de la pantalla.
   */
  const medir = useCallback(() => {
    if (!img.current || !caja.current) return;
    const ri = img.current.getBoundingClientRect();
    const rc = caja.current.getBoundingClientRect();
    if (ri.width < 1) return;
    setCaja2({ x: ri.left - rc.left, y: ri.top - rc.top, w: ri.width });
  }, []);

  useEffect(() => {
    if (!caja.current) return;
    const ro = new ResizeObserver(medir);
    ro.observe(caja.current);
    return () => ro.disconnect();
  }, [medir]);

  /** De píxeles de pantalla a píxeles de la IMAGEN, que es donde se guarda. */
  const aFoto = (ev: React.PointerEvent): Punto => {
    const r = img.current!.getBoundingClientRect();
    const f = nat!;
    return [
      ((ev.clientX - r.left) / r.width) * f.w,
      ((ev.clientY - r.top) / r.height) * f.h,
    ];
  };

  const cambiar = (nuevas: MarcaData[]) => { setMarcas(nuevas); setSucio(true); };

  // `CapaMarcas` pinta filas de la base, con su id. Acá las marcas son una lista
  // en memoria, así que el ÍNDICE hace de id: alcanza para seleccionar, agarrar
  // y pintar el halo, que es todo lo que la capa necesita de él.
  const comoFilas = useMemo<Marca[]>(() => marcas.map(comoFila), [marcas]);

  /** De los puntos del gesto a la marca, con el mismo `marcado.ts` del plano. */
  const armar = (pts: Punto[]): MarcaData | null => {
    if (herramienta === 'borrar') return null;
    const h = herramienta;
    if (DE_TRAZO.includes(h)) return featureDeTrazo(h, adelgazarTrazo(pts, 2), color, grosor);
    if (pts.length < 2) return null;
    const a = pts[0];
    const b = pts[pts.length - 1];
    // Un toque con la nube activa dejaba una nube de lado cero: una mancha de
    // festones sobre sí misma que después no se puede ni agarrar para borrar.
    if (Math.hypot(b[0] - a[0], b[1] - a[1]) < MINIMO_PX) return null;
    return featureDeArrastre(h, a, b, color, grosor);
  };

  /**
   * Repinta el dibujo elegido al cambiar color o grosor, en vez de solo preparar
   * el próximo trazo. Es el gesto de F-2 en el plano, con su misma función:
   * `reestilar` sabe que la nube viaja sin relleno y que el resaltador tiene su
   * grosor multiplicado. Sin esto, corregir un color pedía borrar y redibujar.
   */
  const aplicarEstilo = (c: string, g: number) => {
    if (sel === null) return;
    cambiar(marcas.map((m, i) => (i === sel ? reestilar(m, c, g) : m)));
  };

  const seleccionable = herramienta === 'mover' || herramienta === 'borrar';

  const alBajar = (ev: React.PointerEvent) => {
    if (!nat || ev.button === 2) return;
    ev.currentTarget.setPointerCapture?.(ev.pointerId);
    const p = aFoto(ev);

    // Seleccionar y borrar no dibujan: el toque en el vacío solo deselecciona.
    // El toque SOBRE un dibujo no llega acá, lo atiende `alAgarrar` de la capa.
    if (seleccionable) { setSel(null); return; }

    if (DE_TEXTO.includes(herramienta as Herramienta)) {
      const t = prompt('Texto de la anotación');
      if (t?.trim()) cambiar([...marcas, featureDeTexto(herramienta as Herramienta, p, t.trim(), color)]);
      return;
    }
    vivo.current = { pts: [p] };
  };

  // El dibujo tocado: con la goma se va, con el cursor se agarra para moverlo.
  // El puntero se captura en el propio nodo del dibujo para que el arrastre no
  // se corte al salirse de la figura, que con una flecha fina es en seguida.
  const alAgarrar = (id: number, ev: React.PointerEvent) => {
    if (herramienta === 'borrar') {
      cambiar(marcas.filter((_, i) => i !== id));
      setSel(null);
      return;
    }
    (ev.currentTarget as Element).setPointerCapture?.(ev.pointerId);
    setSel(id);
    const p = aFoto(ev);
    vivo.current = { mover: id, ox: p[0], oy: p[1] };
  };

  const alMover = (ev: React.PointerEvent) => {
    const v = vivo.current;
    if (!v || !nat) return;
    const p = aFoto(ev);

    if ('mover' in v) {
      setCorrida({ id: v.mover, dx: p[0] - v.ox, dy: p[1] - v.oy });
      return;
    }
    // El arrastre solo necesita los dos extremos; el trazo libre, todo el
    // recorrido. Guardar siempre el recorrido y recortarlo al final deja UN
    // solo camino de puntero en vez de dos.
    if (DE_ARRASTRE_FOTO.includes(herramienta)) v.pts[1] = p;
    else v.pts.push(p);
    setPrevia(armar(v.pts));
  };

  const alSubir = () => {
    const v = vivo.current;
    vivo.current = null;
    if (!v) return;

    if ('mover' in v) {
      const d = corrida;
      setCorrida(null);
      if (!d || (d.dx === 0 && d.dy === 0)) return;
      cambiar(marcas.map((m, i) => (i === v.mover ? trasladar(m, d.dx, d.dy) : m)));
      return;
    }
    setPrevia(null);
    const f = armar(v.pts);
    if (f) cambiar([...marcas, f]);
  };

  const cerrar = () => {
    if (sucio && !confirm('Hay anotaciones sin guardar. ¿Salir y perderlas?')) return;
    onCerrar();
  };

  // El SVG se pinta en píxeles de la IMAGEN y se escala al tamaño en pantalla.
  // Así las marcas no se recalculan nunca al cambiar el tamaño de la ventana, y
  // el trazo y el texto se ven iguales en cualquier pantalla.
  const escala = caja2 && nat ? caja2.w / nat.w : 1;

  return (
    // El `data-` es el agarre de la prueba: detrás de este panel sigue montada
    // la capa de marcas DEL PLANO, así que contar marcas sin acotar el ámbito
    // mezclaba los dos dibujos.
    // Fondo casi opaco y no traslúcido: con 85% se leían por detrás la barra de
    // herramientas del plano y la banda de calibrar, justo a la altura del
    // encabezado y del pie del editor. Dos juegos de botones superpuestos en
    // una pantalla de celular es un dedo tocando el que no era.
    <div data-editor-foto className="pointer-events-auto absolute inset-0 z-40 flex flex-col bg-black/95">
      <div className="flex items-center gap-2 border-b border-white/10 px-3 py-2">
        <Camera size={16} weight="bold" className="text-brand-gray-100" />
        <p className="flex-1 truncate text-sm font-semibold text-brand-gray-100">
          {foto.titulo || 'Foto de obra'}
        </p>
        <span className="font-mono text-[11px] text-brand-gray-300">{diaDe(foto)}</span>
        {onBorrar && (
          <button type="button" title="Quitar la foto del plano"
            onClick={() => { if (confirm('¿Quitar esta foto del plano?')) onBorrar(foto.id); }}
            className="rounded p-1 text-brand-red-100 hover:bg-white/15">
            <Trash size={14} weight="bold" />
          </button>
        )}
        <button type="button" onClick={cerrar} aria-label="Cerrar"
          className="rounded p-1 text-brand-gray-300 hover:bg-white/15">
          <X size={14} weight="bold" />
        </button>
      </div>

      {/* Los `pointermove`/`pointerup` van en el MARCO y no en la capa de gestos:
          agarrar un dibujo arranca en el nodo del SVG, que es hermano de esa
          capa, así que sus movimientos solo se encuentran con la otra rama acá
          arriba. Con los tres handlers juntos abajo, mover una marca no movía
          nada. */}
      <div
        ref={caja}
        className="relative flex min-h-0 flex-1 touch-none select-none items-center justify-center"
        onPointerMove={alMover}
        onPointerUp={alSubir}
        onPointerCancel={alSubir}
      >
        {foto.url
          ? <img
              ref={img}
              src={foto.url}
              alt={foto.titulo || 'Foto de obra'}
              onLoad={e => {
                setNat({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight });
                medir();
              }}
              // No la AGRANDA si es más chica que la pantalla, que es la otra
              // cara de dejar que se mida sola. Una foto de celular siempre
              // sobra, y ampliar una chiquita solo la deja borrosa y pixelada.
              className="max-h-full max-w-full"
              // La imagen no recibe punteros: los atiende la capa de gestos. Sin
              // esto, en Chrome arrastrar sobre la foto arranca el «arrastrar
              // imagen» del navegador y el trazo muere en el primer
              // `pointermove` — el mismo mordisco que F-2 encontró en el SVG.
              style={{ pointerEvents: 'none' }}
              draggable={false}
            />
          // Pasa cuando la firma de lectura del blob no se pudo generar. Decirlo
          // es mejor que dejar el marco roto del <img>.
          : <p className="p-6 text-center text-xs text-brand-gray-300">
              No se pudo abrir el archivo de esta foto.
            </p>}

        {/* La capa del gesto. Cubre el marco entero y no solo la foto: un trazo
            que arranca en la franja negra y entra a la imagen es un gesto
            normal, y cortarlo ahí se siente como un error. Lo que caiga fuera
            de la imagen queda clipeado por el `viewBox`, que es lo correcto. */}
        {nat && (
          <div className="absolute inset-0"
               style={{ cursor: seleccionable ? 'default' : 'crosshair' }}
               onPointerDown={alBajar} />
        )}

        {nat && caja2 && (
          // `pointerEvents: none` es lo que deja pasar el dibujo a la capa de
          // abajo. Los nodos de las marcas se lo vuelven a encender ellos
          // mismos cuando hay con qué agarrarlas (ver `CapaMarcas`).
          <div className="absolute" style={{
            left: caja2.x, top: caja2.y, pointerEvents: 'none',
            transform: `scale(${escala})`, transformOrigin: 'top left',
          }}>
            <CapaMarcas
              anchoPx={nat.w}
              altoPx={nat.h}
              marcas={comoFilas}
              escala={escala}
              // Una foto no tiene escala: sin esto, una medida se rotularía con
              // metros inventados. No hay herramienta de medir por lo mismo.
              metrosPorPixel={null}
              idSeleccionada={sel}
              onAgarrar={seleccionable ? alAgarrar : undefined}
              desplazada={corrida}
            />
            {/* La marca que se está dibujando ahora. Va en su propio SVG para no
                re-parsear la lista entera en cada `pointermove`. */}
            {previa && (
              <CapaMarcas anchoPx={nat.w} altoPx={nat.h} escala={escala}
                marcas={[comoFila(previa, -1)]} />
            )}
          </div>
        )}

        <BarraVertical botones={BOTONES} activa={herramienta}
          onElegir={h => { setHerramienta(h); setSel(null); setEstilos(false); }}>
          {/* Color y grosor en UN botón y no en ocho: con la paleta desplegada la
              barra vertical no cabe en un iPhone sin scroll, y una barra que hay
              que scrollear para elegir el lápiz deja de ser una barra. */}
          <button type="button" data-estilos title="Color y grosor"
            onClick={() => setEstilos(v => !v)}
            className="mt-0.5 h-9 w-9 rounded-lg border-2 border-white/25"
            style={{ background: color }} />
        </BarraVertical>

        {estilos && (
          <div className="absolute right-14 top-2 z-20 flex flex-col gap-2 rounded-xl
                          border border-white/10 bg-black/90 p-2">
            <div className="flex gap-1.5">
              {PALETA_COLORES.map(c => (
                <button key={c} type="button" title={`Color ${c}`} data-color={c}
                  onClick={() => { setColor(c); aplicarEstilo(c, grosor); }}
                  className={`h-7 w-7 rounded-full ${color === c ? 'ring-2 ring-white' : ''}`}
                  style={{ background: c }} />
              ))}
            </div>
            <div className="flex items-center gap-1.5">
              {/* El grosor se muestra como lo que es: un punto de ese grosor. */}
              {GROSORES.map(g => (
                <button key={g.valor} type="button" title={g.etiqueta} data-grosor={g.valor}
                  onClick={() => { setGrosor(g.valor); aplicarEstilo(color, g.valor); }}
                  className={`flex h-7 w-7 items-center justify-center rounded-lg ${
                    grosor === g.valor ? 'bg-brand-green-100' : 'bg-white/15'}`}>
                  <span className="block rounded-full"
                    style={{
                      width: Math.min(g.valor + 3, 16), height: Math.min(g.valor + 3, 16),
                      background: grosor === g.valor ? '#111' : '#EBEBEB',
                    }} />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Las acciones del dibujo elegido. Flotan sobre la foto y no van en la
            barra: son del objeto, no de la herramienta, y en la barra estarían
            apagadas el 90% del tiempo. */}
        {sel !== null && (
          <div className="absolute bottom-3 left-1/2 z-20 flex -translate-x-1/2 gap-1 rounded-full
                          border border-white/10 bg-black/85 p-1">
            <button type="button" data-accion="duplicar"
              onClick={() => {
                // Corrido y no encima: un duplicado exacto se ve como que el
                // botón no hizo nada.
                cambiar([...marcas, trasladar(marcas[sel], 18, 18)]);
                setSel(marcas.length);
              }}
              className="rounded-full px-3 py-1.5 text-xs font-semibold text-brand-gray-100 hover:bg-white/15">
              Duplicar
            </button>
            <button type="button" data-accion="eliminar"
              onClick={() => { cambiar(marcas.filter((_, i) => i !== sel)); setSel(null); }}
              className="rounded-full px-3 py-1.5 text-xs font-semibold text-brand-red-100 hover:bg-white/15">
              Eliminar
            </button>
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 border-t border-white/10 px-3 py-2">
        <p className="flex-1 text-[11px] text-brand-gray-300">
          {marcas.length === 0
            ? 'Dibujá sobre la foto; la original no se modifica.'
            : `${marcas.length} dibujo${marcas.length === 1 ? '' : 's'}${sucio ? ' · sin guardar' : ''}`}
        </p>
        {/* El `data-` lo distingue de la X del encabezado, que dice lo mismo
            («Cerrar») y es el otro botón con ese nombre accesible. */}
        <button type="button" data-cerrar onClick={cerrar}
          className="rounded-lg border border-white/20 px-3 py-1.5 text-xs font-semibold text-brand-gray-200">
          {sucio ? 'Descartar' : 'Cerrar'}
        </button>
        {onGuardar && (
          <button type="button" data-guardar
            disabled={!sucio || guardando}
            onClick={() => { onGuardar(foto.id, marcas); setSucio(false); }}
            className="rounded-lg bg-brand-green-100 px-3 py-1.5 text-xs font-semibold text-black
                       disabled:opacity-40">
            {guardando ? 'Guardando…' : 'Guardar anotaciones'}
          </button>
        )}
      </div>
    </div>
  );
}

/** La fecha que importa es cuándo se tomó, no cuándo alguien la subió. */
function diaDe(f: Foto): string {
  const iso = f.fechaTomada || f.fechaCreacion;
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('es-CR');
}
